import type { PendingImport } from 'modules/manager/ui/hooks/use-import-templates';
import React, { useCallback, useState } from 'react';
import UDCheckbox from 'ud-form/components/checkbox/component';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface ImportConflictsModalProps {
  pending: PendingImport;
  /** Receives the `entryIndex` of every template to overwrite; an empty list is valid. */
  onConfirm: (entryIndexes: number[]) => void;
  onCancel: () => void;
}

function conflictsSubtitle(count: number): string {
  if (count === 1) return 'Шаблон из файла называется так же, как уже существующий. Перезаписать его содержимым из файла?';
  const mod10 = count % 10;
  const mod100 = count % 100;
  const word = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'шаблона' : 'шаблонов';
  return `В файле ${count} ${word} с именами, которые уже заняты. Отметьте, какие перезаписать содержимым из файла.`;
}

/**
 * One decision for every same-named template in the file. Nothing is checked by
 * default: overwriting cannot be undone, and for presets it reaches every
 * organization at once — so it is a deliberate choice, never the default.
 */
function ImportConflictsModal({ pending, onConfirm, onCancel }: ImportConflictsModalProps) {
  const { conflicts, isPreset } = pending;
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const allSelected = selected.size === conflicts.length;

  const toggle = useCallback((entryIndex: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(entryIndex)) next.delete(entryIndex);
      else next.add(entryIndex);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(conflicts.map((conflict) => conflict.entryIndex)));
  }, [allSelected, conflicts]);

  return (
    <Modal show onClose={onCancel} size="lg">
      <Modal.Header
        title={isPreset ? 'Такие пресеты уже есть' : 'Такие шаблоны уже есть'}
        subtitle={conflictsSubtitle(conflicts.length)}
      />
      <Modal.Body className="flex flex-col gap-3">
        {isPreset && (
          <UDText variant="body-3" className="bg-CasePendingSurface text-CasePendingText rounded-card px-3 py-2">
            Перезаписанный пресет сразу изменится у всех организаций. Отменить перезапись нельзя.
          </UDText>
        )}

        {/* A div, not a label: `UDCheckbox` is a label itself, and nested labels toggle twice. */}
        <div className="border-lineSubtle flex items-center gap-3 border-b pb-2">
          <UDCheckbox checked={allSelected} onChange={toggleAll} aria-label="Выбрать все" />
          <span className="cursor-pointer" onClick={toggleAll}>
            <UDText variant="body-2" className="text-inkPrimary font-semibold">
              Выбрать все
            </UDText>
          </span>
        </div>

        {/* No scroll of its own: `UDModal` already scrolls past 90vh, and a nested
            `overflow-y: auto` showed a scrollbar for a single row — its content pokes a
            pixel out of the box. */}
        <ul className="flex flex-col gap-2" aria-label="Совпавшие шаблоны">
          {conflicts.map((conflict) => {
            const isChecked = selected.has(conflict.entryIndex);
            return (
              <li key={conflict.entryIndex}>
                <div className="flex items-center gap-3">
                  <UDCheckbox
                    checked={isChecked}
                    onChange={() => toggle(conflict.entryIndex)}
                    aria-label={`Перезаписать «${conflict.existingName}»`}
                  />
                  <span
                    className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-4"
                    onClick={() => toggle(conflict.entryIndex)}
                  >
                    <UDText variant="body-2" className="text-inkPrimary truncate">
                      {conflict.existingName}
                    </UDText>
                    <UDText variant="body-3" className="text-inkSecondary shrink-0">
                      {isChecked
                        ? 'будет перезаписан'
                        : conflict.fallbackName
                          ? `создастся как «${conflict.fallbackName}»`
                          : 'будет пропущен'}
                    </UDText>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="secondary" onClick={onCancel}>
          Отмена
        </UDButton>
        <UDButton variant="primary" onClick={() => onConfirm(Array.from(selected))}>
          {selected.size > 0 ? `Импортировать и перезаписать ${selected.size}` : 'Импортировать'}
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportConflictsModal;
